// Embedded adapter — runs inside a NetSuite Suitelet that serves index.html.
//
// All CRUD round-trips go back to the same Suitelet URL with an `action`
// query param. The Suitelet (deployments/embedded/suitelet.js) dispatches to
// SuiteScript modules (N/record, N/search, N/auth) on the server side, so the
// browser never holds NetSuite credentials.
//
// The Suitelet URL is exposed by the host page as `window.__SUITELET_URL__`
// before the SPA boots; if absent we fall back to current location.

function suiteletUrl() {
  if (typeof window !== "undefined" && window.__SUITELET_URL__) {
    return window.__SUITELET_URL__;
  }
  return window.location.pathname + window.location.search;
}

async function call(action, payload, method = "POST") {
  const url = new URL(suiteletUrl(), window.location.origin);
  url.searchParams.set("action", action);
  const opts = {
    method,
    headers: { "Content-Type": "application/json" },
    credentials: "include",
  };
  if (method !== "GET" && payload !== undefined) {
    opts.body = JSON.stringify(payload);
  } else if (method === "GET" && payload) {
    Object.entries(payload).forEach(([k, v]) => url.searchParams.set(k, v));
  }
  const res = await fetch(url.toString(), opts);
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Suitelet ${action} failed: ${res.status} ${text}`);
  }
  return res.json();
}

export const embeddedAdapter = {
  name: "embedded",

  // Auth — embedded mode trusts the NetSuite session cookie. The Suitelet
  // resolves the current user via N/runtime.
  async getSession() { return call("session.get", null, "GET"); },
  async login() { throw new Error("embedded mode uses NetSuite SSO; no client login"); },
  async logout() { return call("session.logout"); },

  // Catalog
  async listProducts(params) { return call("catalog.list", params, "GET"); },
  async getProduct(id) { return call("catalog.get", { id }, "GET"); },

  // Availability. `mode` selects the server-side source:
  //   "b2b"    — custom record subset (default)
  //   "public" — item-level qty (public storefront)
  async getAvailability(sku, mode) { return call("inventory.get", { sku, mode }, "GET"); },

  // Cart — embedded carts persist as a custom record, keyed by user.
  async getCart() { return call("cart.get", null, "GET"); },
  async updateCart(items) { return call("cart.update", { items }); },

  // Quotes (NetSuite Estimate record).
  async listQuotes() { return call("quote.list", null, "GET"); },
  async getQuote(id) { return call("quote.get", { id }, "GET"); },
  async createQuote(payload) { return call("quote.create", payload); },
  async submitQuote(id) { return call("quote.submit", { id }); },

  // Orders (NetSuite SalesOrder record).
  async listOrders() { return call("order.list", null, "GET"); },
  async getOrder(id) { return call("order.get", { id }, "GET"); },
  async convertQuoteToOrder(quoteId) {
    return call("order.fromQuote", { quoteId });
  },
  async submitSalesOrder(payload)    { return call("order.submitSO", payload); },
  async submitPurchaseOrder(payload) { return call("order.submitPO", payload); },

  // Customer / B2B account.
  async getAccount() { return call("customer.get", null, "GET"); },
  async updateAccount(patch) { return call("customer.update", patch); },

  // Bolt checkout token — Suitelet brokers the create-cart call so secret
  // keys stay on the NetSuite side.
  async createBoltCart(payload) { return call("bolt.createCart", payload); },
  async createStripeIntent(payload) { return call("stripe.createIntent", payload); },
};
